/**
 * Error Page
 * Generic error page for 500 and other server errors
 */

import { useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { AlertCircle, Home, RefreshCcw } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

export function ErrorPage() {
  const navigate = useNavigate();
  const error = useRouteError();

  let errorMessage = 'An unexpected error occurred. Please try again.';
  let errorStatus = 500;
  let errorStatusText = 'Internal Server Error';

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorStatusText = error.statusText;
    errorMessage = error.data?.message || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center text-center py-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10 mb-6">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>

            <h1 className="scroll-m-20 text-4xl/tight font-bold tracking-tight mb-2">
              {errorStatus}
            </h1>

            <h2 className="scroll-m-20 text-xl/snug font-semibold tracking-tight mb-4">
              {errorStatusText}
            </h2>

            <p className="text-sm/5 text-muted-foreground mb-6 max-w-sm">
              {errorMessage}
            </p>

            {process.env.NODE_ENV !== "production" && error instanceof Error && error.stack && (
              <Alert variant="destructive" className="mb-6 text-left">
                <AlertDescription className="font-mono text-xs overflow-auto max-h-40">
                  {error.stack}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-2 w-full sm:flex-row sm:justify-center">
              <Button onClick={handleReload} className="w-full sm:w-auto">
                <RefreshCcw className="mr-2 h-4 w-4" />
                Reload Page
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <Home className="mr-2 h-4 w-4" />
                Go to Dashboard
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
